import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { HandHeart, ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { api, formatApiErrorDetail } from "../lib/auth";

const INTERESTS = [
  "peer_support", "events", "resource_research",
  "community_welcome", "fundraising", "admin_support",
];
const AVAILABILITY = [
  "weekday_mornings", "weekday_afternoons",
  "weekday_evenings", "weekends",
];

function toggleIn(list, v) {
  const s = new Set(list || []);
  s.has(v) ? s.delete(v) : s.add(v);
  return Array.from(s);
}

export default function VolunteerApply() {
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", location: "",
    motivation: "", interests: [], availability: [],
    agreement_accepted: false,
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.agreement_accepted) return toast.error("Please accept the volunteer agreement.");
    setBusy(true);
    try {
      await api.post("/ops/public/volunteer-application", form);
      setDone(true);
      toast.success("Your volunteer application is with BTC.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not submit.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="bg-white min-h-screen">
        <Header />
        <main className="max-w-3xl mx-auto px-6 pt-32 pb-24 text-center">
          <CheckCircle2 className="w-16 h-16 text-[#23803F] mx-auto mb-6" />
          <h1 className="text-4xl font-extrabold text-[#0B132B] mb-4">Thank you.</h1>
          <p className="text-lg text-[#4B5563] mb-8">
            Your volunteer application is with the BTC team. We will be in touch
            by email as soon as a coordinator reviews your submission.
          </p>
          <Link to="/" className="btc-btn-primary" data-testid="volunteer-apply-home">
            Back to BTC <ArrowRight className="w-4 h-4" />
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen" data-testid="volunteer-apply-page">
      <Header />
      <main className="max-w-3xl mx-auto px-6 pt-28 pb-20">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-xl bg-[#1A5FAB]/10 flex items-center justify-center">
            <HandHeart className="w-5 h-5 text-[#1A5FAB]" strokeWidth={1.75} />
          </div>
          <p className="btc-eyebrow">VOLUNTEER WITH BTC</p>
        </div>
        <h1 className="text-4xl font-extrabold text-[#0B132B] mb-3">Lend your time.</h1>
        <p className="text-[#4B5563] leading-relaxed mb-10">
          Volunteers keep BTC running — from peer-support helpers to event hosts,
          researchers and welcomers. Tell us how you'd like to help.
        </p>
        <form onSubmit={submit} className="space-y-6" data-testid="volunteer-apply-form">
          <Field label="Full name" required>
            <input required value={form.full_name} onChange={(e) => set("full_name", e.target.value)} className="btc-input" data-testid="volunteer-apply-name" />
          </Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Email" required>
              <input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="btc-input" data-testid="volunteer-apply-email" />
            </Field>
            <Field label="Phone">
              <input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="btc-input" data-testid="volunteer-apply-phone" />
            </Field>
          </div>
          <Field label="Location / service area">
            <input value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="City, region, country" className="btc-input" data-testid="volunteer-apply-location" />
          </Field>
          <Field label="Why do you want to volunteer with BTC?" required>
            <textarea required minLength={10} rows={5} value={form.motivation} onChange={(e) => set("motivation", e.target.value)} className="btc-input" data-testid="volunteer-apply-motivation" />
          </Field>
          <div>
            <label className="block text-sm font-semibold text-[#0B132B] mb-3">Areas of interest</label>
            <div className="flex flex-wrap gap-2">
              {INTERESTS.map((i) => (
                <button
                  type="button" key={i}
                  data-testid={`volunteer-interest-${i}`}
                  onClick={() => set("interests", toggleIn(form.interests, i))}
                  className={`px-4 py-2 rounded-full border text-sm font-semibold transition ${form.interests.includes(i) ? "bg-[#1A5FAB] text-white border-[#1A5FAB]" : "border-gray-200 text-[#4B5563]"}`}
                >{i.replace(/_/g, " ")}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-[#0B132B] mb-3">Availability</label>
            <div className="flex flex-wrap gap-2">
              {AVAILABILITY.map((a) => (
                <button
                  type="button" key={a}
                  data-testid={`volunteer-availability-${a}`}
                  onClick={() => set("availability", toggleIn(form.availability, a))}
                  className={`px-4 py-2 rounded-full border text-sm font-semibold transition ${form.availability.includes(a) ? "bg-[#1A5FAB] text-white border-[#1A5FAB]" : "border-gray-200 text-[#4B5563]"}`}
                >{a.replace(/_/g, " ")}</button>
              ))}
            </div>
          </div>
          <label className="flex items-start gap-3 p-4 border border-gray-200 rounded-xl">
            <input type="checkbox" checked={form.agreement_accepted} onChange={(e) => set("agreement_accepted", e.target.checked)} data-testid="volunteer-apply-agreement" className="mt-1" />
            <span className="text-sm text-[#4B5563]">I understand BTC will review my application and contact me. I agree to keep BTC members' information confidential and to represent BTC with care.</span>
          </label>
          <button type="submit" disabled={busy} className="btc-btn-primary w-full justify-center" data-testid="volunteer-apply-submit">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</> : <>Submit application <ArrowRight className="w-4 h-4" /></>}
          </button>
        </form>
      </main>
      <Footer />
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-semibold text-[#0B132B] mb-2">
        {label}{required && <span className="text-[#B00020]"> *</span>}
      </span>
      {children}
    </label>
  );
}
